/**
 * BL-EPIC-239. Twilio Lookup results translated into Sam's phone status pill.
 * The member app carries a copy of this mapping; keep the two in step.
 */
export interface PhoneLookupFields {
  phone?: string | null;
  phoneType?: string | null;
  phoneLineStatus?: string | null;
  phoneLookupStatus?: string | null;
  phoneLookupCheckedAt?: string | null;
}

export type PhonePillTone = 'green' | 'yellow' | 'red';

export interface PhonePill {
  label: string;
  tone: PhonePillTone;
}

const statusWord = (lineStatus?: string | null) => {
  switch (lineStatus?.toLowerCase()) {
    case 'active':
    case 'reachable':
      return 'Verified';
    case 'inactive':
    case 'unreachable':
      return 'Inactive';
    default:
      return 'Unverified';
  }
};

const typeWord = (phoneType?: string | null) => {
  switch (phoneType?.toLowerCase()) {
    case 'mobile':
      return 'Mobile';
    case 'landline':
      return 'Landline';
    case 'fixedvoip':
    case 'nonfixedvoip':
      return 'VoIP';
    default:
      return 'Unknown Type';
  }
};

/**
 * `null` when the number has never been checked: until the lookup is switched
 * on every member is in that state, and the UI must look exactly as it did.
 */
export const phonePill = (
  fields?: PhoneLookupFields | null,
): PhonePill | null => {
  if (!fields?.phone || !fields.phoneLookupStatus) return null;

  const status = statusWord(fields.phoneLineStatus);
  const type = typeWord(fields.phoneType);

  // Sam's rules, first match wins: only "Verified Mobile" is ever green.
  const tone: PhonePillTone =
    type === 'Landline' || type === 'VoIP' || status === 'Inactive'
      ? 'red'
      : status === 'Unverified' || type === 'Unknown Type'
      ? 'yellow'
      : 'green';

  return { label: `${status} ${type}`, tone };
};

/** The words the Phone Status filter offers; the API matches on these. */
export const PHONE_STATUS_FILTER_OPTIONS = [
  'Verified',
  'Inactive',
  'Unverified',
  'Mobile',
  'Landline',
  'VoIP',
  'Unknown Type',
].map((word) => ({ value: word, label: word }));
